import { Binary, canonical } from "./binary.js";

// Valve DMX binary 2 (TF2's PCFs) and the later binary 3–5 string dictionaries.
// Element references are resolved only after reading every element, including
// hidden/name-independent children. No executable content is evaluated.
export function parsePcf(bytes) {
    const r = new Binary(bytes); let offset = 0;
    const take = size => { const at = offset; r.check(at, size); offset += size; return at; };
    const u8 = () => r.u8(take(1)), u16 = () => r.u16(take(2)), i32 = () => r.i32(take(4)), f32 = () => r.f32(take(4));
    const string = () => { const s = r.string(offset, 65536); offset += new TextEncoder().encode(s).length + 1; return s; };
    const header = string();
    const match = /^<!-- dmx encoding binary ([1-5]) format pcf \d+ -->\s*$/.exec(header);
    if (!match) throw new Error("Unsupported PCF encoding");
    const version = +match[1], strings = [];
    const count = (n, max = 65536) => { if (n < 0 || n > max) throw new Error("PCF count exceeds budget"); return n; };
    if (version >= 2) for (let n = count(version >= 4 ? i32() : u16()); n--;) strings.push(string());
    const indexed = () => { const index = version >= 5 ? i32() : u16(); if (index < 0 || index >= strings.length) throw new Error("Invalid PCF string index"); return strings[index]; };
    const key = () => version >= 2 ? indexed() : string();
    const elements = Array.from({ length: count(i32(), 32768) }, () => {
        const type = key(), name = version >= 4 ? indexed() : string(), id = Array.from(r.slice(take(16), 16), x => x.toString(16).padStart(2, "0")).join("");
        return { type, name, id, attributes: Object.create(null) };
    });
    let values = 0;
    const scalar = type => {
        if (++values > 1000000) throw new Error("PCF attribute budget exceeded");
        switch (type) {
            case 1: { const index = i32(); if (index === -1) return null; if (index === -2) { string(); return null; } if (index < 0 || index >= elements.length) throw new Error("Invalid PCF element reference"); return elements[index]; }
            case 2: return i32();
            case 3: return f32();
            case 4: return !!u8();
            case 5: return version >= 4 ? indexed() : string();
            case 6: { const n = count(i32(), 16 * 1024 * 1024); return r.slice(take(n), n).slice(); }
            case 7: return i32() / 10000;
            case 8: return [u8(), u8(), u8(), u8()];
            case 9: return [f32(), f32()];
            case 10: case 12: return [f32(), f32(), f32()];
            case 11: case 13: return [f32(), f32(), f32(), f32()];
            case 14: return Array.from({ length: 16 }, f32);
            default: throw new Error(`Unsupported PCF attribute type ${type}`);
        }
    };
    for (const element of elements) for (let n = count(i32(), 4096); n--;) {
        const name = key(), type = u8();
        if (type === 6) { const length = count(i32(), 16 * 1024 * 1024); element.attributes[name] = r.slice(take(length), length).slice(); }
        else if (type >= 15 && type <= 28) element.attributes[name] = Array.from({ length: count(i32()) }, () => scalar(type - 14));
        else element.attributes[name] = scalar(type);
    }
    if (offset !== bytes.length) throw new Error("Unexpected PCF bytes");
    return elements.filter(e => e.type === "DmeParticleSystemDefinition");
}

export function particlePath(path) {
    const p = canonical(path.replace(/^!/, ""));
    if (!p.startsWith("particles/") || !p.endsWith(".pcf")) throw new Error("Invalid particle asset path");
    return p;
}

/** Flatten cyclic DMX graphs into cloneable definitions keyed by UUID. */
export function particleDefinitions(elements) {
    return elements.map(e => {
        const a = e.attributes;
        const definition = { id: e.id, name: e.name, ...a };
        for (const list of ["emitters", "initializers", "operators", "forces", "constraints", "renderers"])
            definition[list] = (a[list] ?? []).filter(Boolean).map(op => ({ ...op.attributes, name: op.attributes.functionName ?? op.name }));
        definition.children = (a.children ?? []).filter(c => c?.attributes.child).map(c => ({ id: c.attributes.child.id, name: c.attributes.child.name,
            delay: c.attributes["delay"] ?? 0, endCap: c.attributes["end cap"] ?? false }));
        return definition;
    });
}

// psheet.cpp: VTF resource 0x10 holds sheet sequences, per-frame durations,
// and four image rectangles in version 1 (one in version 0).
export function parseParticleSheet(bytes) {
    const r = new Binary(bytes); let o = 0;
    const int = () => { const v = r.i32(o); o += 4; return v; }, float = () => { const v = r.f32(o); o += 4; return v; };
    const version = int(), count = int(), sequences = [];
    if (version < 0 || version > 1 || count < 0 || count > 64) throw new Error("Invalid particle sheet");
    for (let i = 0; i < count; i++) {
        const id = int(), clamp = !!int(), count = int(), duration = float(), frames = [];
        if (id < 0 || id >= 64 || count < 1 || count > 4096 || duration < 0) throw new Error("Invalid particle sheet sequence");
        for (let j = 0; j < count; j++) {
            const time = float(), rects = Array.from({ length: version ? 4 : 1 }, () => [float(), float(), float(), float()]);
            if (time < 0) throw new Error("Invalid particle sheet duration");
            frames.push({ time, rects });
        }
        sequences[id] = { clamp, duration: frames.reduce((n, f) => n + f.time, 0), frames };
    }
    return sequences;
}

export function sheetFrame(sheet, sequence, age, rate = 1, fit = false, lifetime = 1) {
    const s = sheet?.[sequence] ?? sheet?.find(Boolean);
    if (!s) return { a: [0, 0, 1, 1], b: [0, 0, 1, 1], blend: 0 };
    let time = fit ? age / Math.max(lifetime, 0.0001) * s.duration : age * rate;
    time = s.clamp ? Math.min(time, s.duration - 0.000001) : s.duration ? time % s.duration : 0;
    let index = 0;
    while (index < s.frames.length - 1 && time >= s.frames[index].time) time -= s.frames[index++].time;
    const next = s.clamp ? Math.min(index + 1, s.frames.length - 1) : (index + 1) % s.frames.length;
    return { a: s.frames[index].rects[0], b: s.frames[next].rects[0], blend: s.frames[index].time ? time / s.frames[index].time : 0 };
}
